import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Loader2, MailX, CheckCircle2 } from 'lucide-react';
import api from '../services/api';
import { useTenant } from '../context/TenantContext';

/**
 * Public unsubscribe landing page.
 *
 * Reached from the "Unsubscribe" link in the footer of every marketing
 * campaign. The token in the URL is HMAC-signed by the backend — no
 * login or DB lookup needed. One-click confirms unsubscribe.
 */
export default function Unsubscribe() {
  const { branding } = useTenant();
  const tenantName = branding?.business_name || 'this academy';
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [state, setState] = useState({ loading: true, email: null, error: null, done: false });

  useEffect(() => {
    if (!token) {
      setState({ loading: false, error: 'Missing token in URL', email: null, done: false });
      return;
    }
    api.get(`/email/unsubscribe/verify?token=${encodeURIComponent(token)}`)
      .then(({ data }) => setState({ loading: false, email: data.email, error: null, done: false }))
      .catch((e) => setState({ loading: false, error: e?.response?.data?.detail || 'Invalid or expired link', email: null, done: false }));
  }, [token]);

  const confirm = async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      await api.post('/email/unsubscribe', { token });
      setState((s) => ({ ...s, loading: false, done: true }));
    } catch (e) {
      setState((s) => ({ ...s, loading: false, error: e?.response?.data?.detail || 'Failed to unsubscribe' }));
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <Card className="max-w-md w-full" data-testid="unsubscribe-page">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {state.done ? <CheckCircle2 className="h-6 w-6 text-emerald-600" /> : <MailX className="h-6 w-6 text-rose-600" />}
            {state.done ? "You're unsubscribed" : 'Unsubscribe from marketing emails'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {state.loading && (
            <div className="flex items-center gap-2 text-slate-500"><Loader2 className="h-4 w-4 animate-spin" />Verifying link…</div>
          )}
          {state.error && (
            <div className="text-rose-700 bg-rose-50 border border-rose-200 rounded p-3 text-sm">{state.error}</div>
          )}
          {!state.loading && !state.error && !state.done && state.email && (
            <>
              <p className="text-sm text-slate-700">
                Click the button below to unsubscribe <strong>{state.email}</strong> from all marketing emails from {tenantName}.
              </p>
              <p className="text-xs text-slate-500">
                You will still receive transactional emails about your enrollments, payments, and registrations — these are required for the services you've signed up for and cannot be turned off.
              </p>
              <Button onClick={confirm} className="w-full" data-testid="confirm-unsubscribe-btn">
                Yes, unsubscribe me
              </Button>
            </>
          )}
          {state.done && (
            <>
              <p className="text-sm text-slate-700">
                <strong>{state.email}</strong> has been removed from our marketing email list. You won't receive any further newsletters or promotional emails from us.
              </p>
              <p className="text-xs text-slate-500">
                If this was a mistake, contact us and we'll restore your subscription.
              </p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
